// /eqms — Sprachdemo eQMS-Agent (Vorführung 24.10.2026). Chat-Optik nach dem Muster der Claude-App (Handy).
//
// Kopfzeile · Verlauf (Nutzer rechts, Agent als Fließtext mit Markdown, darüber eine aufklappbare Schrittzeile, die
// live wächst) · Composer unten: Textfeld mit Mikrofon und rundem Senden-Pfeil. Während der Aufnahme wird die
// Fußzeile zu [✕ abbrechen] [Live-Wellenform] [■ Stopp] [↑ senden]; das Transkript läuft live ins Textfeld
// (/api/eqms-demo/voice, rollierende Gemini-Transkription) und bleibt editierbar.
// Senden → /api/eqms-demo/ask (SSE: ack · step · tick · delta · done | error).
//
// Login: Deep-Link /eqms#k=<token> wie /fair (Token in localStorage "sb_token"; der Hash bleibt in der URL, damit
// das Homescreen-Lesezeichen ihn trägt). Manifest: index.html wählt eqms.webmanifest beim Parsen.
import { FormEvent, useCallback, useEffect, useRef, useState } from "react";
import { ApiError, api, apiUrl, authToken, setToken } from "../api";
import Md from "../components/Md";

const CHIPS = [
  { label: "eQMS-Frage",
    text: "Welche Prüfungen verlangen wir für die Biokompatibilität eines Medizinprodukts nach ISO 10993-1?" },
  { label: "Normvergleich",
    text: "Was ändert sich mit der ISO 17665 gegenüber der alten Ausgabe und welche unserer Dokumente sind betroffen?" },
];

const C = {
  bg: "#faf9f5", text: "#1f1e1d", muted: "#73726c", line: "#e8e6dc", bubble: "#f0eee6",
  accent: "#c96442", link: "#2c5aa0", gap: "#b42318",
};

interface Ack { mode: string; task: string; approach?: string[]; eta_text?: string | null; replay?: boolean }
interface Turn {
  id: number; question: string; ack: Ack | null; steps: string[]; elapsed: number; answer: string;
  state: "running" | "done" | "error"; error: string | null;
  runId?: string; nEvents: number;          // server run + count of ack/step events seen (resume index)
}
interface RunRow { id: string; question: string; kind: string; status: "running" | "done" | "failed"; created_at: string; elapsed_s: number | null }
interface RunFull extends RunRow { ack: Ack | null; steps: string[]; answer: string | null; error: string | null; n_events: number | null }

const CSS =
  "@keyframes eq-spin{to{transform:rotate(360deg)}} .eq-spin{animation:eq-spin .9s linear infinite}" +
  " .eq-app textarea:focus-visible,.eq-app textarea:focus{box-shadow:none;outline:none}" +
  " .eq-md{font-size:15.5px;line-height:1.55;color:#1f1e1d} .eq-md table{display:block;overflow-x:auto;font-size:12.5px}" +
  " .eq-md h3{font-size:16px;font-weight:650;margin:14px 0 6px}";

// ── Audio: PCM16/16 kHz-Stücke im Ping-Pong an /voice + Pegel für die Wellenform ───────────────────
function toPcm16(input: Float32Array, rate: number): Int16Array {
  const ratio = rate / 16000;
  const n = Math.floor(input.length / ratio);
  const out = new Int16Array(n);
  for (let i = 0; i < n; i++) {
    const s = Math.max(-1, Math.min(1, input[Math.floor(i * ratio)]));
    out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return out;
}

function concat(parts: Int16Array[]): Uint8Array {
  const out = new Int16Array(parts.reduce((a, p) => a + p.length, 0));
  let o = 0;
  for (const p of parts) {
    out.set(p, o);
    o += p.length;
  }
  return new Uint8Array(out.buffer);
}

async function postVoice(sid: string, body: Uint8Array, final: boolean): Promise<string> {
  const r = await fetch(apiUrl(`/api/eqms-demo/voice/${sid}?final=${final ? 1 : 0}`), {
    method: "POST",
    headers: { "Content-Type": "application/octet-stream", Authorization: `Bearer ${authToken() ?? ""}` },
    body: body as unknown as BodyInit,
  });
  if (!r.ok) throw new ApiError(r.status, `voice ${r.status}`);
  return ((await r.json()) as { text: string }).text;
}

function clientLog(event: string, detail = "") {
  void fetch(apiUrl("/api/eqms-demo/client-log"), {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken() ?? ""}` },
    body: JSON.stringify({ event, detail: detail.slice(0, 1500), ua: navigator.userAgent.slice(0, 380) }),
  }).catch(() => undefined);
}

const BARS = 34;

function useRecorder(onText: (t: string) => void) {
  const [recording, setRecording] = useState(false);
  const [levels, setLevels] = useState<number[]>([]);
  const [error, setError] = useState<string | null>(null);
  const st = useRef<{
    ctx: AudioContext; stream: MediaStream; node: ScriptProcessorNode; pending: Int16Array[]; sid: string;
    stop: boolean; loop: Promise<void>; timer: number;
  } | null>(null);

  // iOS Safari: the AudioContext must be created and resumed INSIDE the tap. Created after `await getUserMedia`
  // it stays "suspended" and `await ctx.resume()` never settles — no audio, no /voice request (prod log 23.10.).
  const start = useCallback(() => {
    setError(null);
    const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    let ctx: AudioContext;
    try {
      ctx = new Ctx();
    } catch (e) {
      clientLog("mic_ctx_error", String(e));
      setError("Audio wird von diesem Browser nicht unterstützt.");
      return;
    }
    void ctx.resume().catch(() => undefined);
    setLevels([]);
    setRecording(true);                       // immediate feedback; reverted on failure
    clientLog("mic_start", `ctx=${ctx.state} rate=${ctx.sampleRate}`);
    navigator.mediaDevices.getUserMedia({ audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true } })
      .then((stream) => {
        if (ctx.state !== "running") void ctx.resume().catch(() => undefined);
        const src = ctx.createMediaStreamSource(stream);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 1024;
        src.connect(analyser);
        const node = ctx.createScriptProcessor(4096, 1, 1);
        const pending: Int16Array[] = [];
        let frames = 0;
        node.onaudioprocess = (e) => {
          if (frames++ === 0) clientLog("mic_audio_ok", `ctx=${ctx.state} rate=${ctx.sampleRate}`);
          pending.push(toPcm16(e.inputBuffer.getChannelData(0), ctx.sampleRate));
        };
        src.connect(node);
        node.connect(ctx.destination);
        const buf = new Uint8Array(analyser.fftSize);
        const timer = window.setInterval(() => {
          analyser.getByteTimeDomainData(buf);
          let sum = 0;
          for (const v of buf) sum += ((v - 128) / 128) ** 2;
          setLevels((l) => [...l.slice(-(BARS - 1)), Math.min(1, Math.sqrt(sum / buf.length) * 6)]);
        }, 90);
        const sid = Math.random().toString(36).slice(2, 12);
        const s = { ctx, stream, node, pending, sid, stop: false, loop: Promise.resolve(), timer };
        window.setTimeout(() => {
          if (!s.stop && frames === 0) {
            clientLog("mic_no_audio", `ctx=${ctx.state}`);
            setError("Kein Ton vom Mikrofon — bitte ■ und erneut 🎙 tippen.");
          }
        }, 2500);
        s.loop = (async () => {
          // Ping-Pong: das nächste Stück erst nach der Antwort aufs vorige (~1–1,5 s Takt).
          let first = true;
          while (!s.stop) {
            await new Promise((r) => setTimeout(r, 400));
            if (s.stop || !s.pending.length) continue;
            try {
              const t = await postVoice(sid, concat(s.pending.splice(0)), false);
              if (first && t) { clientLog("voice_text_ok", t.slice(0, 40)); first = false; }
              if (!s.stop && t) onText(t);
            } catch (e) {
              clientLog("voice_error", String(e));
              setError(e instanceof ApiError && e.status === 401 ? "Zugang ungültig." : "Transkription gestört.");
            }
          }
        })();
        st.current = s;
      })
      .catch((e: unknown) => {
        const err = e as { name?: string; message?: string };
        clientLog("mic_error", `${err.name}: ${err.message}`);
        void ctx.close();
        setRecording(false);
        setError(err.name === "NotAllowedError"
          ? "Mikrofon nicht erlaubt — in den Safari-Einstellungen für atlas.bitop.de freigeben."
          : `Kein Mikrofon-Zugriff (${err.name ?? "Fehler"}).`);
      });
  }, [onText]);

  const finish = useCallback(async (cancel: boolean): Promise<string | null> => {
    const s = st.current;
    setRecording(false);
    if (!s) return null;
    st.current = null;
    s.stop = true;
    setRecording(false);
    window.clearInterval(s.timer);
    await s.loop;
    s.node.disconnect();
    s.stream.getTracks().forEach((t) => t.stop());
    void s.ctx.close();
    try {
      const t = await postVoice(s.sid, cancel ? new Uint8Array() : concat(s.pending.splice(0)), true);
      return cancel ? null : t;
    } catch {
      return null;
    }
  }, []);

  return { recording, levels, error, start, finish };
}

// ── SSE ─────────────────────────────────────────────────────────────────────────────
async function streamSSE(path: string, body: unknown | null, on: (ev: string, data: Record<string, unknown>) => void) {
  const r = await fetch(apiUrl(path), {
    method: body == null ? "GET" : "POST",
    headers: { "Content-Type": "application/json", Accept: "text/event-stream", Authorization: `Bearer ${authToken() ?? ""}` },
    body: body == null ? undefined : JSON.stringify(body),
  });
  if (!r.ok || !r.body) throw new ApiError(r.status, `HTTP ${r.status}`);
  const reader = r.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    let i: number;
    while ((i = buf.indexOf("\n\n")) !== -1) {
      const frame = buf.slice(0, i);
      buf = buf.slice(i + 2);
      let ev = "message";
      let data = "";
      for (const line of frame.split("\n")) {
        if (line.startsWith("event:")) ev = line.slice(6).trim();
        else if (line.startsWith("data:")) data += line.slice(5).trim();
      }
      if (data) on(ev, JSON.parse(data));
    }
  }
}

// ── Icons ───────────────────────────────────────────────────────────────────────────
const svg = (d: React.ReactNode, size = 22, sw = 2) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={sw}
    strokeLinecap="round" strokeLinejoin="round" aria-hidden>{d}</svg>
);
const MenuIcon = () => svg(<path d="M4 7h16M4 12h16M4 17h10" />, 22, 2);
const MicIcon = () => svg(<><rect x="9" y="3" width="6" height="11" rx="3" /><path d="M5 11a7 7 0 0 0 14 0M12 18v3" /></>);
const UpIcon = () => svg(<path d="M12 19V5M6 11l6-6 6 6" />, 22, 2.4);
const XIcon = () => svg(<path d="M6 6l12 12M18 6L6 18" />, 22, 2.2);
const Chevron = ({ open }: { open: boolean }) => (
  <span style={{ display: "inline-block", transform: open ? "rotate(90deg)" : "none", transition: "transform .15s" }}>
    {svg(<path d="M9 6l6 6-6 6" />, 14, 2.4)}
  </span>
);
const Spin = () => (
  <span className="eq-spin inline-block h-[14px] w-[14px] shrink-0 rounded-full border-2"
    style={{ borderColor: "#d6dbe6", borderTopColor: C.link }} />
);

function Waveform({ levels }: { levels: number[] }) {
  const cells = [...Array(Math.max(0, BARS - levels.length)).fill(0), ...levels];
  return (
    <div className="flex h-[36px] min-w-0 flex-1 items-center justify-end gap-[3px] overflow-hidden" aria-label="Pegel">
      {cells.map((v, i) => v < 0.06
        ? <span key={i} className="h-[3px] w-[3px] shrink-0 rounded-full" style={{ background: C.text, opacity: 0.55 }} />
        : <span key={i} className="w-[3px] shrink-0 rounded-full" style={{ background: C.text, height: `${Math.round(6 + v * 28)}px` }} />)}
    </div>
  );
}

const when = (iso: string) => {
  const d = new Date(iso);
  const today = new Date().toDateString() === d.toDateString();
  return today ? d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })
    : d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" }) + " " + d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
};
const STATUS = { running: "läuft", done: "fertig", failed: "fehlgeschlagen" } as const;

function Sidebar({ open, runs, activeRun, onClose, onOpen, onNew }: {
  open: boolean; runs: RunRow[]; activeRun: string | null;
  onClose: () => void; onOpen: (id: string) => void; onNew: () => void;
}) {
  const startX = useRef<number | null>(null);
  return (
    <>
      <div onClick={onClose} aria-hidden className="fixed inset-0 z-30 transition-opacity"
        style={{ background: "rgba(0,0,0,.28)", opacity: open ? 1 : 0, pointerEvents: open ? "auto" : "none" }} />
      <nav aria-label="Verlauf" aria-hidden={!open}
        onTouchStart={(e) => { startX.current = e.touches[0].clientX; }}
        onTouchMove={(e) => { if (startX.current != null && startX.current - e.touches[0].clientX > 60) { startX.current = null; onClose(); } }}
        className="fixed inset-y-0 left-0 z-40 flex w-[82%] max-w-[330px] flex-col border-r transition-transform duration-200"
        style={{ background: C.bg, borderColor: C.line, transform: open ? "none" : "translateX(-102%)",
          paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}>
        <div className="flex h-[56px] shrink-0 items-center px-4 text-[16px] font-semibold">Verlauf</div>
        <div className="flex-1 overflow-y-auto px-2">
          {runs.length === 0 && <div className="px-3 py-2 text-[14px]" style={{ color: C.muted }}>Noch keine Aufträge.</div>}
          {runs.map((r) => (
            <button key={r.id} type="button" onClick={() => onOpen(r.id)}
              className="mb-1 block w-full rounded-[12px] px-3 py-[9px] text-left"
              style={{ background: r.id === activeRun ? C.bubble : "transparent" }}>
              <div className="truncate text-[14.5px]">{r.question}</div>
              <div className="mt-[2px] flex items-center gap-2 text-[12.5px]" style={{ color: C.muted }}>
                <span>{when(r.created_at)}</span>
                <span style={{ color: r.status === "failed" ? C.gap : r.status === "running" ? C.link : C.muted }}>
                  {r.status === "running" && <span className="eq-spin mr-1 inline-block h-[9px] w-[9px] rounded-full border-[1.5px] align-[-1px]"
                    style={{ borderColor: "#d6dbe6", borderTopColor: C.link }} />}
                  {STATUS[r.status]}
                </span>
              </div>
            </button>
          ))}
        </div>
        <div className="shrink-0 border-t p-3" style={{ borderColor: C.line }}>
          <button type="button" onClick={onNew} className="w-full rounded-full border bg-white py-[10px] text-[15px] font-medium"
            style={{ borderColor: C.line }}>+ Neue Sitzung</button>
        </div>
      </nav>
    </>
  );
}

function KeyScreen({ error, onSubmit }: { error: string | null; onSubmit: (k: string) => void }) {
  const [k, setK] = useState("");
  return (
    <div className="eq-app fixed inset-0 flex flex-col items-center justify-center px-6" style={{ background: C.bg, color: C.text }}>
      <style>{CSS}</style>
      <form className="w-full max-w-[360px]" onSubmit={(e) => { e.preventDefault(); if (k.trim()) onSubmit(k.trim()); }}>
        <div className="text-[20px] font-semibold">eQMS-Agent</div>
        <label htmlFor="eq-key" className="mt-6 block text-[14px]" style={{ color: C.muted }}>Zugangsschlüssel</label>
        <input id="eq-key" value={k} onChange={(e) => setK(e.target.value)} autoComplete="off" autoCapitalize="off"
          spellCheck={false} className="mt-2 w-full rounded-[14px] border bg-white px-4 py-3 text-[16px] outline-none"
          style={{ borderColor: C.line }} />
        {error && <div className="mt-3 text-[14px]" style={{ color: C.gap }}>{error}</div>}
        <button type="submit" disabled={!k.trim()} className="mt-4 h-[48px] w-full rounded-[14px] text-[16px] font-semibold text-white disabled:opacity-40"
          style={{ background: C.accent }}>Weiter</button>
      </form>
    </div>
  );
}

const mmss = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

function StepsRow({ t }: { t: Turn }) {
  const [open, setOpen] = useState(false);
  const running = t.state === "running" && !t.answer;
  const current = t.steps[t.steps.length - 1];
  // prod doc-ai sends no intermediate steps: then the row shows the agent's plan (ack.approach), labelled as such
  const plan = t.steps.length === 0 ? (t.ack?.approach ?? []) : [];
  const items = t.steps.length ? t.steps : plan;
  const head = running
    ? (current ?? (t.ack ? "Agent recherchiert im eQMS …" : "Auftrag wird verstanden …"))
    : t.steps.length ? `${t.steps.length} Schritte · ${mmss(t.elapsed)}` : `Recherche · ${mmss(t.elapsed)}`;
  return (
    <div className="mb-2">
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open}
        className="flex w-full items-center gap-2 text-left text-[15px]" style={{ color: C.link }}>
        {running ? <Spin /> : <Chevron open={open} />}
        <span className="min-w-0 flex-1 truncate">{head}</span>
        {running && <span className="shrink-0 text-[13px]" style={{ color: C.muted }}>{mmss(t.elapsed)}</span>}
        {running && items.length > 0 && <Chevron open={open} />}
      </button>
      {open && items.length > 0 && (
        <ol className="mt-2 flex flex-col gap-[6px] border-l-2 pl-3" style={{ borderColor: C.line }}>
          {plan.length > 0 && <li className="text-[12.5px] font-semibold" style={{ color: C.muted }}>Vorgehen</li>}
          {items.map((s, i) => (
            <li key={i} className="text-[13.5px] leading-[1.35]"
              style={{ color: /: Lücke$/.test(s) ? C.gap : C.muted, fontWeight: /: Lücke$/.test(s) ? 600 : 400 }}>{s}</li>
          ))}
        </ol>
      )}
    </div>
  );
}

function TurnView({ t }: { t: Turn }) {
  return (
    <div className="flex flex-col gap-4">
      <div className="max-w-[85%] self-end whitespace-pre-wrap break-words rounded-[18px] px-4 py-[10px] text-[15.5px] leading-[1.45]"
        style={{ background: C.bubble }}>{t.question}</div>
      <div>
        {t.ack && (
          <div className="mb-1 text-[13px]" style={{ color: C.muted }}>
            {t.ack.mode === "norm_compare" ? "Normvergleich" : "eQMS-Agent"}{t.ack.replay ? " · Aufzeichnung" : ""}
            {t.ack.eta_text && t.state === "running" && !t.answer ? ` · Dauer ${t.ack.eta_text}` : ""}
          </div>
        )}
        <StepsRow t={t} />
        {t.answer && <div className="eq-md"><Md>{t.answer}</Md></div>}
        {t.state === "error" && <div className="mt-2 text-[14px]" style={{ color: C.gap }}>Fehler: {t.error}</div>}
      </div>
    </div>
  );
}

export default function EqmsPage() {
  const [auth, setAuth] = useState<"checking" | "ok" | "nokey" | "offline">("checking");
  const [keyError, setKeyError] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [turns, setTurns] = useState<Turn[]>([]);
  const scrollRef = useRef<HTMLDivElement>(null);
  const taRef = useRef<HTMLTextAreaElement>(null);
  const prefixRef = useRef("");
  const sendSeq = useRef(0);   // a late final transcript must not refill the field after the question was sent
  const onText = useCallback((t: string) => {
    const p = prefixRef.current;
    setQ(p ? `${p} ${t}` : t);
  }, []);
  const rec = useRecorder(onText);
  const busy = turns.some((t) => t.state === "running");
  const [runs, setRuns] = useState<RunRow[]>([]);
  const [drawer, setDrawer] = useState(false);
  const edgeX = useRef<number | null>(null);
  const loadRuns = useCallback(async () => {
    try {
      setRuns(await api.get<RunRow[]>("/api/eqms-demo/runs"));
    } catch {
      /* sidebar stays as it was */
    }
  }, []);

  useEffect(() => {
    const old = document.title;
    document.title = "eQMS-Agent";
    return () => { document.title = old; };
  }, []);

  const checkKey = useCallback(async () => {
    setAuth("checking");
    try {
      await api.get<{ name: string }>("/auth/me");
      setAuth("ok");
      void api.post("/api/eqms-demo/warmup").catch(() => undefined);
    } catch (e) {
      if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
        setToken(null);
        setKeyError("Dieser Zugangsschlüssel ist ungültig.");
        setAuth("nokey");
      } else setAuth("offline");
    }
  }, []);

  useEffect(() => {
    const k = new URLSearchParams(window.location.hash.replace(/^#/, "")).get("k");
    if (k) setToken(k);
    if (!localStorage.getItem("sb_token")) {
      setAuth("nokey");
      return;
    }
    void checkKey();
  }, [checkKey]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [turns]);

  // Transkript: Cursor ans Ende, Feld wächst mit (max. ~7 Zeilen).
  useEffect(() => {
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = `${Math.min(ta.scrollHeight, 180)}px`;
    if (rec.recording) {
      ta.scrollTop = ta.scrollHeight;
      if (document.activeElement === ta) ta.setSelectionRange(q.length, q.length);
    }
  }, [q, rec.recording]);

  const update = (id: number, f: (t: Turn) => Turn) => setTurns((ts) => ts.map((t) => (t.id === id ? f(t) : t)));
  const turnsRef = useRef(turns);
  turnsRef.current = turns;

  // One handler for a fresh run and for a re-attached one; counts ack/step events (= server resume index).
  const handler = (id: number) => (ev: string, d: Record<string, unknown>) => {
    if (ev === "run") update(id, (t) => ({ ...t, runId: String(d.id) }));
    else if (ev === "ack") update(id, (t) => ({ ...t, ack: d as unknown as Ack, nEvents: t.nEvents + 1 }));
    else if (ev === "step") update(id, (t) => ({ ...t, steps: [...t.steps, String(d.text)], nEvents: t.nEvents + 1 }));
    else if (ev === "delta") update(id, (t) => ({ ...t, answer: t.answer + String(d.text) }));
    else if (ev === "done") update(id, (t) => ({ ...t, state: "done" }));
    else if (ev === "error") update(id, (t) => ({ ...t, state: "error", error: String(d.message) }));
  };

  // Follow a run until it ends; a dropped stream (iOS sleeps the page) re-attaches from the last index.
  const follow = async (id: number, first: () => Promise<void>, elapsed0 = 0) => {
    const t0 = Date.now() - elapsed0 * 1000;
    const timer = window.setInterval(() => update(id, (t) => ({ ...t, elapsed: Math.round((Date.now() - t0) / 1000) })), 1000);
    try {
      let attempt = first;
      for (let tries = 0; tries < 60; tries++) {
        try {
          await attempt();
          await new Promise((r) => setTimeout(r, 60));          // let the last events render into turnsRef
          const cur = turnsRef.current.find((t) => t.id === id);
          if (cur?.runId && cur.state === "running") throw new Error("stream ended early");   // iOS cut it silently
          break;
        } catch (err) {
          const cur = turnsRef.current.find((t) => t.id === id);
          if (!cur?.runId || cur.state !== "running") throw err;
          clientLog("stream_reattach", `${(err as Error).message} run=${cur.runId} n=${cur.nEvents}`);
          await new Promise((r) => setTimeout(r, document.hidden ? 3000 : 1500));
          const c2 = turnsRef.current.find((t) => t.id === id)!;
          update(id, (t) => ({ ...t, answer: "" }));          // the answer is streamed again in full
          attempt = () => streamSSE(`/api/eqms-demo/runs/${c2.runId}/events?start=${c2.nEvents}`, null, handler(id));
        }
      }
      update(id, (t) => (t.state === "running" && t.answer ? { ...t, state: "done" } : t));
    } catch (err) {
      update(id, (t) => ({ ...t, state: "error", error: (err as Error).message }));
    } finally {
      window.clearInterval(timer);
      void loadRuns();
    }
  };

  const openRun = async (runId: string) => {
    setDrawer(false);
    try {
      const r = await api.get<RunFull>(`/api/eqms-demo/runs/${runId}`);
      const id = Date.now();
      const running = r.status === "running" && r.n_events != null;
      const elapsed = r.elapsed_s ?? Math.max(0, Math.round((Date.now() - Date.parse(r.created_at)) / 1000));
      setTurns([{ id, question: r.question, ack: r.ack, steps: r.steps, answer: r.answer ?? "",
        elapsed,
        state: running ? "running" : r.status === "failed" ? "error" : "done",
        error: r.error, runId: r.id, nEvents: r.n_events ?? 0 }]);
      if (running)
        void follow(id, () => streamSSE(`/api/eqms-demo/runs/${r.id}/events?start=${r.n_events}`, null, handler(id)), elapsed);
    } catch (e) {
      clientLog("open_run_error", String(e));
    }
  };

  // After login: sidebar data; a run still running (e.g. page was reloaded mid-run) is reopened right away.
  useEffect(() => {
    if (auth !== "ok") return;
    void (async () => {
      try {
        const rs = await api.get<RunRow[]>("/api/eqms-demo/runs");
        setRuns(rs);
        if (rs[0]?.status === "running" && turnsRef.current.length === 0) void openRun(rs[0].id);
      } catch {
        /* ignore */
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth]);

  const startRec = () => {
    prefixRef.current = q.trim();
    void rec.start();
  };
  const stopRec = async () => {
    const seq = sendSeq.current;
    const t = await rec.finish(false);
    if (t && seq === sendSeq.current) onText(t);
    taRef.current?.focus();
  };
  const cancelRec = async () => {
    await rec.finish(true);
    setQ(prefixRef.current);
  };

  const send = async (e?: FormEvent) => {
    e?.preventDefault();
    let question = q.trim();
    if (rec.recording) {
      const t = await rec.finish(false);
      if (t) question = (prefixRef.current ? `${prefixRef.current} ${t}` : t).trim();
    }
    if (!question || busy) return;
    sendSeq.current += 1;
    const id = Date.now();
    setTurns((ts) => [...ts, { id, question, ack: null, steps: [], elapsed: 0, answer: "", state: "running", error: null, nEvents: 0 }]);
    setQ("");
    window.setTimeout(() => void loadRuns(), 1500);
    await follow(id, () => streamSSE("/api/eqms-demo/ask", { question }, handler(id)));
  };

  if (auth === "nokey")
    return <KeyScreen error={keyError} onSubmit={(k) => {
      setToken(k);
      window.history.replaceState(null, "", `${window.location.pathname}#k=${encodeURIComponent(k)}`);
      setKeyError(null);
      void checkKey();
    }} />;

  const canSend = (!!q.trim() || rec.recording) && !busy && auth === "ok";
  const round = "flex h-[44px] w-[44px] shrink-0 items-center justify-center rounded-full";

  return (
    <div className="eq-app fixed inset-0 flex flex-col" style={{ background: C.bg, color: C.text,
      paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}
      onTouchStart={(e) => { edgeX.current = e.touches[0].clientX < 28 ? e.touches[0].clientX : null; }}
      onTouchMove={(e) => { if (edgeX.current != null && e.touches[0].clientX - edgeX.current > 60) { edgeX.current = null; setDrawer(true); void loadRuns(); } }}>
      <style>{CSS}</style>
      <Sidebar open={drawer} runs={runs} activeRun={turns.length === 1 ? turns[0].runId ?? null : null}
        onClose={() => setDrawer(false)} onOpen={(id) => void openRun(id)}
        onNew={() => { setDrawer(false); setTurns([]); setQ(""); }} />
      <header className="relative flex h-[56px] shrink-0 items-center justify-center border-b px-4" style={{ borderColor: C.line }}>
        <button type="button" aria-label="Verlauf öffnen" onClick={() => { setDrawer(true); void loadRuns(); }}
          className="absolute left-2 flex h-[40px] w-[40px] items-center justify-center rounded-full" style={{ color: C.text }}>
          <MenuIcon />
        </button>
        <div className="text-center leading-tight">
          <div className="text-[16.5px] font-semibold">eQMS-Agent</div>
          <div className="text-[12.5px]" style={{ color: C.muted }}>
            {auth === "offline" ? "keine Verbindung" : "doc-ai"}
          </div>
        </div>
        {turns.length > 0 && !busy && (
          <button type="button" onClick={() => setTurns([])} className="absolute right-3 rounded-full border px-3 py-[5px] text-[13px]"
            style={{ borderColor: C.line, color: C.text }}>Neu</button>
        )}
      </header>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 pb-4 pt-5">
        <div className="mx-auto flex max-w-[720px] flex-col gap-7">
          {turns.length === 0 && (
            <div className="pt-[18vh] text-center text-[22px] font-medium" style={{ color: C.text }}>
              Was möchtest du wissen?
              <div className="mt-2 text-[14.5px] font-normal" style={{ color: C.muted }}>Sprechen oder tippen.</div>
            </div>
          )}
          {turns.map((t) => <TurnView key={t.id} t={t} />)}
        </div>
      </div>

      <div className="shrink-0 px-3 pb-3">
        <div className="mx-auto max-w-[720px]">
          {turns.length === 0 && (
            <div className="mb-2 flex gap-2 overflow-x-auto">
              {CHIPS.map((c) => (
                <button key={c.label} type="button" onClick={() => setQ(c.text)}
                  className="shrink-0 rounded-full border bg-white px-4 py-[7px] text-[14px]" style={{ borderColor: C.line }}>
                  {c.label}
                </button>
              ))}
            </div>
          )}
          <form onSubmit={send} className="rounded-[26px] border bg-white px-3 pb-2 pt-3 shadow-[0_1px_6px_rgba(0,0,0,.06)]"
            style={{ borderColor: C.line }}>
            <textarea ref={taRef} value={q} onChange={(e) => setQ(e.target.value)} rows={1} aria-label="Frage"
              placeholder={rec.recording ? "Sprechen …" : "Frage an den eQMS-Agenten"}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); void send(); } }}
              className="block max-h-[180px] w-full resize-none bg-transparent px-1 text-[16px] leading-[1.45]" />
            {rec.error && <div className="px-1 pt-1 text-[12.5px]" style={{ color: C.gap }}>{rec.error}</div>}
            <div className="mt-2 flex items-center gap-2">
              {rec.recording ? (
                <>
                  <button type="button" aria-label="Aufnahme abbrechen" onClick={() => void cancelRec()}
                    className={round} style={{ background: C.bubble }}><XIcon /></button>
                  <Waveform levels={rec.levels} />
                  <button type="button" aria-label="Aufnahme beenden" onClick={() => void stopRec()}
                    className={round} style={{ background: C.bubble }}>
                    <span className="block h-[14px] w-[14px] rounded-[3px]" style={{ background: C.text }} />
                  </button>
                </>
              ) : (
                <>
                  <button type="button" aria-label="Sprechen" onClick={startRec} disabled={busy || auth !== "ok"}
                    className={round + " disabled:opacity-40"} style={{ color: C.text }}><MicIcon /></button>
                  <div className="flex-1" />
                </>
              )}
              <button type="submit" aria-label="Senden" disabled={!canSend}
                className={round + " text-white disabled:opacity-35"} style={{ background: C.accent }}><UpIcon /></button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
